/**
 * Edit Mode Manager
 *
 * Manages edit mode changes and applies restrictions based on field types.
 * Uses field-level categorization (no AI classification) for determining
 * which elements can be edited in restricted modes.
 */

import { editModeService } from './EditModeService';
import { EDIT_MODE } from '@frontend/constants/edit-mode';

class EditModeManager {
  constructor() {
    this._onModeChangeCallbacks = [];
  }

  /**
   * Register a callback for mode changes
   */
  onModeChange(callback) {
    this._onModeChangeCallbacks.push(callback);
  }

  /**
   * Notify all callbacks of mode change
   */
  _notifyModeChange(mode) {
    this._onModeChangeCallbacks.forEach(cb => cb(mode, false));
  }

  /**
   * Change edit mode and apply restrictions based on field types
   * @param {string} newMode - The new edit mode
   * @param {Object} options - Options object
   * @param {Array} options.previews - Canvas previews array
   * @param {number} options.layoutId - Layout ID for saving mode
   * @returns {Promise<boolean>} - True if successful
   */
  async changeEditMode(newMode, { previews, layoutId } = {}) {
    // If switching to full edit, unlock everything
    if (newMode === EDIT_MODE.FULL_EDIT) {
      editModeService.setEditMode(newMode);
      this._applyRestrictions(previews, false);
      this._notifyModeChange(newMode);
      if (layoutId) {
        await this._saveEditModeToDatabase(newMode, layoutId);
      }
      return true;
    }

    // For restricted modes - apply restrictions directly based on fieldType
    // No AI classification needed - relies entirely on existing fieldType properties
    editModeService.setEditMode(newMode);
    this._applyRestrictions(previews, true);
    this._notifyModeChange(newMode);

    if (layoutId) {
      await this._saveEditModeToDatabase(newMode, layoutId);
    }

    return true;
  }

  /**
   * Save edit mode to database
   * @private
   */
  async _saveEditModeToDatabase(mode, layoutId) {
    try {
      await axios.post('/banner-builder/update-edit-mode', {
        layout_id: layoutId,
        edit_mode: mode,
      });
    } catch (error) {
      console.error('[EditModeManager] Failed to save edit mode:', error);
    }
  }

  /**
   * Apply edit mode restrictions to all canvas objects
   * Uses EditModeService.canEditElement() which checks fieldType
   */
  _applyRestrictions(previews, restricted) {
    if (!previews) return;

    for (const preview of previews) {
      if (!preview?.canvas) continue;

      const objects = preview.canvas.getObjects();

      for (const obj of objects) {
        // Skip non-selectable objects
        if (obj.selectable === false) continue;

        if (restricted) {
          const canEdit = editModeService.canEditElement(obj);

          if (!canEdit) {
            obj.lockMovementX = true;
            obj.lockMovementY = true;
            obj.lockScalingX = true;
            obj.lockScalingY = true;
            obj.lockRotation = true;
            obj.hasControls = false;
            obj.hoverCursor = 'not-allowed';
          } else {
            // Ensure editable elements are unlocked
            obj.lockMovementX = false;
            obj.lockMovementY = false;
            obj.lockScalingX = false;
            obj.lockScalingY = false;
            obj.lockRotation = false;
            obj.hasControls = true;
            obj.hoverCursor = 'move';
          }
        } else {
          // Full edit mode - unlock everything
          obj.lockMovementX = false;
          obj.lockMovementY = false;
          obj.lockScalingX = false;
          obj.lockScalingY = false;
          obj.lockRotation = false;
          obj.hasControls = true;
          obj.hoverCursor = 'move';
        }
      }

      preview.canvas.requestRenderAll();
    }
  }
}

// Singleton instance
export const editModeManager = new EditModeManager();

export default editModeManager;
